import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { toLocalDateStr, getWeekRange, getMonthRange } from '../utils/dateRange';

export type DashboardPeriod = 'today' | 'week' | 'month' | 'quarter';

export interface UseDashboardPeriodReturn {
  period: DashboardPeriod;
  setPeriod: (p: DashboardPeriod) => void;
  /** Human label for the period, e.g. 'Today', 'This week' */
  label: string;
  /** The start/end dates for the sessions list query (frontend-computed) */
  sessionDateRange: { startDate: string; endDate: string };
  /** The period string to send to the backend KPI queries */
  kpiPeriod: string;
}

const VALID_PERIODS: DashboardPeriod[] = ['today', 'week', 'month', 'quarter'];

const LABELS: Record<DashboardPeriod, string> = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
  quarter: 'This quarter',
};

const KPI_PERIODS: Record<DashboardPeriod, string> = {
  today: 'day',
  week: 'week',
  month: 'month',
  quarter: 'quarter',
};

/** localStorage key for persisting the dashboard period preference. */
const STORAGE_KEY = "portal-dashboard-period";

function isValidPeriod(value: string | null): value is DashboardPeriod {
  return value !== null && VALID_PERIODS.includes(value as DashboardPeriod);
}

/** Load period from localStorage, or null if not found/invalid/unavailable. */
function loadFromStorage(): DashboardPeriod | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return null;
    if (!isValidPeriod(stored)) return null;
    return stored;
  } catch {
    // Storage unavailable — fail silently.
    return null;
  }
}

function getQuarterRange(): { start: Date; end: Date } {
  const now = new Date();
  const quarterMonth = Math.floor(now.getMonth() / 3) * 3; // 0, 3, 6, 9
  const start = new Date(now.getFullYear(), quarterMonth, 1);
  const end = new Date(now.getFullYear(), quarterMonth + 3, 0); // last day of quarter
  return { start, end };
}

export function useDashboardPeriod(): UseDashboardPeriodReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get('period');

  // localStorage holds the last chosen period; used only to seed the URL on
  // first load when no explicit ?period= is present.
  const storedPeriod = useMemo(() => loadFromStorage(), []);

  // Seed the URL from localStorage once on mount when no param is set, so the
  // URL stays the single source of truth — back/forward and deep links keep
  // working because `period` is always derived from the URL below.
  useEffect(() => {
    if (!isValidPeriod(raw) && storedPeriod && storedPeriod !== 'month') {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('period', storedPeriod);
          return next;
        },
        { replace: true },
      );
    }
    // Mount-only: seeds the initial URL from storage; later changes flow
    // through setPeriod.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const period: DashboardPeriod = isValidPeriod(raw) ? raw : 'month';

  const setPeriod = useCallback(
    (p: DashboardPeriod) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (p === 'month') {
          next.delete('period');
        } else {
          next.set('period', p);
        }
        return next;
      });

      // Remember the choice for the next visit.
      try {
        localStorage.setItem(STORAGE_KEY, p);
      } catch {
        // Storage unavailable — continue anyway.
      }
    },
    [setSearchParams],
  );

  const label = LABELS[period];
  const kpiPeriod = KPI_PERIODS[period];

  const sessionDateRange = useMemo(() => {
    const today = toLocalDateStr(new Date());
    switch (period) {
      case 'today':
        return { startDate: today, endDate: today };
      case 'week': {
        const range = getWeekRange(0);
        return { startDate: toLocalDateStr(range.start), endDate: toLocalDateStr(range.end) };
      }
      case 'month': {
        const range = getMonthRange();
        return { startDate: toLocalDateStr(range.start), endDate: toLocalDateStr(range.end) };
      }
      case 'quarter': {
        const range = getQuarterRange();
        return { startDate: toLocalDateStr(range.start), endDate: toLocalDateStr(range.end) };
      }
    }
  }, [period]);

  return { period, setPeriod, label, sessionDateRange, kpiPeriod };
}
